/* istanbul ignore file */

import { createTheme, formLabelClasses } from '@mui/material';
import { frFR } from '@mui/material/locale';

// Titre, texte courant
const fira = 'Fira Sans Extra Condensed, sans-serif';

// Surtitres, inter-titres, exergues
declare module '@mui/material/Button' {
    interface ButtonPropsVariantOverrides {
        neutral: true;
    }
}

// Style des box
declare module '@mui/material/Box' {
    interface BoxPropsVariantOverrides {
        greyBox: true;
    }
}

// Création de la palette de couleurs
declare module '@mui/material/styles/createPalette' {
    interface PaletteOptions {
        blue?: PaletteColorOptions;
        lightBlue?: PaletteColorOptions;
        skyBlue?: PaletteColorOptions;
        darkBlue?: PaletteColorOptions;
        green?: PaletteColorOptions;
        turquoise?: PaletteColorOptions;
        yellow?: PaletteColorOptions;
        brown?: PaletteColorOptions;
        lightGrey?: PaletteColorOptions;
        darkGrey?: PaletteColorOptions;
    }

    interface Palette {
        blue: PaletteColor;
        lightBlue: PaletteColor;
        skyBlue: PaletteColor;
        darkBlue: PaletteColor;
        green: PaletteColor;
        turquoise: PaletteColor;
        yellow: PaletteColor;
        brown: PaletteColor;
        lightGrey: PaletteColor;
        darkGrey: PaletteColor;
    }
}

/**
 * Ajout des couleurs dans un Theme pour l'ajouter dans le theme principal
 */
let data = createTheme(
    {
        palette: {
            blue: {
                main: '#3766A5',
                light: '#e2e7f2',
            },
            lightBlue: {
                main: '#84c3df',
            },
            skyBlue: {
                main: '#3f83c5',
            },
            darkBlue: {
                main: '#23387b',
            },
            green: {
                main: '#95be72',
            },
            turquoise: {
                main: '#7ebaa7',
            },
            yellow: {
                main: '#eec109',
            },
            brown: {
                main: '#6f492b',
            },
            lightGrey: {
                main: '#e0e0e0',
            },
            darkGrey: {
                main: '#524c41',
            },
        },
    },
    frFR
);

/**
 * Theme avec en paramètre les couleurs
 * Et contenant le design SCSS de chaque component utilisé dans l'application
 */
const theme = createTheme(data, {
    data,
    components: {
        MuiContainer: {
            styleOverrides: {
                root: {
                    padding: '0 !important',
                },
            },
        },
        // Liens hypertexte
        MuiLink: {
            styleOverrides: {
                root: {
                    color: data.palette.blue.main,
                    fontFamily: fira,
                    fontSize: 'italic',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    '&:hover': {
                        color: data.palette.darkGrey.main,
                    },
                },
            },
        },
        // Paper
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundColor: '#fff',
                    borderRadius: '10px',
                    padding: '20px',
                },
            },
        },
        // Cards
        MuiCard: {
            styleOverrides: {
                root: {
                    backgroundColor: '#fff',
                    borderRadius: '3px',
                },
            },
        },
        // Inputs
        MuiInputBase: {
            styleOverrides: {
                input: {
                    fontFamily: fira,
                },
                root: {
                    backgroundColor: 'white',
                },
            },
        },
        // Labels d'inputs
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    fontFamily: fira,
                },
            },
        },
        // Typographies
        MuiTypography: {
            styleOverrides: {
                h1: {
                    marginBottom: '20px',
                    fontFamily: fira,
                    fontSize: 'xxx-large',
                    fontWeight: 'bold',
                    letterSpacing: '2px',
                    [data.breakpoints.down('md')]: {
                        fontSize: 'xx-large',
                    },
                    color: data.palette.blue.main,
                },
                h2: {
                    fontFamily: fira,
                    fontWeight: 'bold',
                    marginTop: '10px',
                    marginBottom: '10px',
                    fontSize: '2em',
                    color: data.palette.blue.main,
                },
                h3: {
                    fontFamily: fira,
                    fontWeight: 'bold',
                    marginBottom: '20px',
                    fontSize: 'x-large',
                    color: data.palette.blue.main,
                },
                h4: {
                    fontFamily: fira,
                    fontWeight: 'bold',
                    marginBottom: '15px',
                    letterSpacing: '1px',
                    fontSize: 'x-large',
                    color: data.palette.darkGrey.main,
                },
                h5: {
                    fontFamily: fira,
                    marginBottom: '5px',
                    fontSize: 'large',
                    color: data.palette.darkGrey.main,
                },
                h6: {
                    fontFamily: fira,
                    fontSize: 'medium',
                    fontWeight: 'bold',
                    color: data.palette.blue.main,
                },
                body1: {
                    fontFamily: fira,
                    color: data.palette.darkGrey.main,
                },
                body2: {
                    fontFamily: fira,
                    color: data.palette.darkGrey.main,
                },
            },
        },
        // Boutons
        MuiButton: {
            variants: [
                {
                    props: { variant: 'contained' },
                    style: {
                        textAlign: 'center',
                        color: '#fff',
                        backgroundColor: data.palette.blue.main,
                        '&:hover': {
                            color: '#fff',
                            backgroundColor: data.palette.darkGrey.main,
                        },
                        fontFamily: fira,
                        textTransform: 'none',
                    },
                },
                {
                    props: { variant: 'text' },
                    style: {
                        textAlign: 'center',
                        color: data.palette.blue.main,
                        '&:hover': {
                            color: data.palette.blue.main,
                        },
                        fontFamily: fira,
                        textDecoration: 'underline',
                        textTransform: 'none',
                    },
                },
                {
                    props: { variant: 'outlined' },
                    style: {
                        textAlign: 'center',
                        fontFamily: fira,
                        textTransform: 'none',
                        '&:hover': {
                            border: 'none',
                        },
                    },
                },
                {
                    props: { variant: 'neutral' },
                    style: {
                        textAlign: 'center',
                        color: '#fff',
                        backgroundColor: data.palette.darkGrey.main,
                        '&:hover': {
                            color: '#fff',
                            backgroundColor: data.palette.skyBlue.main,
                        },
                        fontFamily: fira,
                        textTransform: 'none',
                    },
                },
            ],
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    marginBottom: '20px',
                    [`&.${formLabelClasses.focused}`]: {
                        borderBottomColor: data.palette.blue.main,
                    },
                    // Default state of underline.
                    '& .MuiInputBase-root.MuiInput-root.MuiInput-underline.MuiInputBase-formControl::before': {
                        borderBottomColor: data.palette.blue.main,
                    },

                    // On hover state of underline.
                    '& .MuiInputBase-root.MuiInput-root.MuiInput-underline.MuiInputBase-formControl:hover::before': {
                        borderBottomColor: data.palette.blue.main,
                    },

                    // On focus state of underline.
                    '& .MuiInputBase-root.MuiInput-root.MuiInput-underline.MuiInputBase-formControl.Mui-focused::after':
                        {
                            borderBottom: '2px solid #f8b334',
                        },
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                root: {
                    marginBottom: '20px',
                    [`&.${formLabelClasses.focused}`]: {
                        borderBottomColor: data.palette.blue.main,
                    },
                    // Default state of underline.
                    '& .MuiInputBase-root.MuiInput-root.MuiInput-underline.MuiInputBase-formControl::before': {
                        borderBottomColor: data.palette.blue.main,
                    },

                    // On hover state of underline.
                    '& .MuiInputBase-root.MuiInput-root.MuiInput-underline.MuiInputBase-formControl:hover::before': {
                        borderBottomColor: data.palette.blue.main,
                    },

                    // On focus state of underline.
                    '& .MuiInputBase-root.MuiInput-root.MuiInput-underline.MuiInputBase-formControl.Mui-focused::after':
                        {
                            borderBottom: '2px solid #f8b334',
                        },
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    marginBottom: '0px',
                    fontWeight: 'bold',
                    backgroundColor: data.palette.blue.main,
                    color: '#fff',
                    borderRadius: '5px',
                    padding: '10px',
                },
            },
        },
    },
});

export default theme;
